import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Trial, TrialIngredient, TrialParameter, Product, Ingredient } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import {
  bakerPctNum,
  formatTrialDate,
  getTrialId,
  getTrialName,
  printWorkOrder,
  sumBakerPct,
} from "./trialUtils";

type TabType = "nalog" | "parametri" | "usporedi" | "izvedba";

const TrialDetailPage = () => {
  savePathToLocalStorage();
  const history = useHistory();
  const { trialId: trialIdParam } = useParams<{ trialId: string }>();
  const trialId = Number(trialIdParam);

  const [trials, setTrials] = useState<Trial[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrial, setSelectedTrial] = useState<Trial | null>(null);
  const [trialIngredients, setTrialIngredients] = useState<TrialIngredient[]>([]);
  const [trialParameters, setTrialParameters] = useState<TrialParameter[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("nalog");
  // One flag for every write action on this page, so none of them can be
  // submitted twice while the request is in flight.
  const [saving, setSaving] = useState(false);

  // Filters removed — list lives on /mes/saved-trials

  // Compare state
  const [compareTrialId, setCompareTrialId] = useState<number | null>(null);
  const [compareTrialData, setCompareTrialData] = useState<Trial | null>(null);
  const [compareIngredients, setCompareIngredients] = useState<TrialIngredient[]>([]);
  const [compareParameters, setCompareParameters] = useState<TrialParameter[]>([]);

  // Parameter form state
  const [paramForm, setParamForm] = useState({
    vrsta_fermentacije: "day",
    masa_zamjesa_kg: "",
    temp_zamjes_cel: "",
    ph_starter: "",
    ph1_masa: "",
    ph2_masa: "",
    h1_masa: "",
    h2_masa: "",
    t_start_mijesanja: "",
    t_kraj_mijesanja: "",
    t_start_pecenje: "",
    t_kraj_pecenje: "",
    temp_pecenje_cel: "",
    spec_vol_lkg: "",
    skart: "",
    skart_boja: "",
    skart_oblik: "",
    napomena: "",
  });

  // Promote state
  const [promoteRecipeCode, setPromoteRecipeCode] = useState("");

  useEffect(() => {
    if (!trialId || Number.isNaN(trialId)) return;
    (async () => {
      setLoading(true);
      try {
        const [trial, trialsData, ingredientsData] = await Promise.all([
          mesApi.getTrial(trialId),
          mesApi.getTrials({}),
          mesApi.getIngredients(),
        ]);
        setTrials(trialsData);
        setIngredients(ingredientsData);
        await loadTrialDetails(trial);
      } catch (err: any) {
        toast.error("Greska pri ucitavanju: " + err.message);
        setSelectedTrial(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [trialId]);

  const loadTrials = async () => {
    try {
      const data = await mesApi.getTrials({});
      setTrials(data);
    } catch {
      /* ignore */
    }
  };

  const loadTrialDetails = async (trial: Trial) => {
    setSelectedTrial(trial);
    setActiveTab("nalog");
    try {
      const [ingredientsData, paramsData] = await Promise.all([
        mesApi.getTrialIngredients(getTrialId(trial)),
        mesApi.getTrialParameters(getTrialId(trial)),
      ]);
      setTrialIngredients(ingredientsData);
      setTrialParameters(paramsData);

      // Pre-fill param form if data exists
      if (paramsData.length > 0) {
        const p = paramsData[0];
        setParamForm({
          vrsta_fermentacije: (p as any).FermentationType || "day",
          masa_zamjesa_kg: String(p.DoughMassKg || ""),
          temp_zamjes_cel: String(p.DoughTempC || ""),
          ph_starter: String(p.StarterPh || ""),
          ph1_masa: String((p as any).BulkFermentationPh1 || ""),
          ph2_masa: String((p as any).BulkFermentationPh2 || ""),
          h1_masa: String((p as any).BulkFermentationHeight1 || ""),
          h2_masa: String((p as any).BulkFermentationHeight2 || ""),
          t_start_mijesanja: (p as any).MixingStart || "",
          t_kraj_mijesanja: (p as any).MixingEnd || "",
          t_start_pecenje: (p as any).BakingStart || "",
          t_kraj_pecenje: (p as any).BakingEnd || "",
          temp_pecenje_cel: String((p as any).BakingTempC || ""),
          spec_vol_lkg: String((p as any).SpecificVolumeLkg || ""),
          skart: String((p as any).Scrap || ""),
          skart_boja: String((p as any).ScrapColor || ""),
          skart_oblik: String((p as any).ScrapShape || ""),
          napomena: p.Notes || "",
        });
      } else {
        resetParamForm();
      }
    } catch (err: any) {
      toast.error("Greska pri ucitavanju detalja: " + err.message);
    }
  };

  const loadCompareTrialData = async (trialId: number) => {
    setCompareTrialId(trialId);
    if (!trialId) {
      setCompareTrialData(null);
      setCompareIngredients([]);
      setCompareParameters([]);
      return;
    }
    try {
      const [trial, ing, params] = await Promise.all([
        mesApi.getTrial(trialId),
        mesApi.getTrialIngredients(trialId),
        mesApi.getTrialParameters(trialId),
      ]);
      setCompareTrialData(trial);
      setCompareIngredients(ing);
      setCompareParameters(params);
    } catch (err: any) {
      toast.error("Greska pri ucitavanju usporedbe: " + err.message);
    }
  };

  const resetParamForm = () => {
    setParamForm({
      vrsta_fermentacije: "day",
      masa_zamjesa_kg: "",
      temp_zamjes_cel: "",
      ph_starter: "",
      ph1_masa: "",
      ph2_masa: "",
      h1_masa: "",
      h2_masa: "",
      t_start_mijesanja: "",
      t_kraj_mijesanja: "",
      t_start_pecenje: "",
      t_kraj_pecenje: "",
      temp_pecenje_cel: "",
      spec_vol_lkg: "",
      skart: "",
      skart_boja: "",
      skart_oblik: "",
      napomena: "",
    });
  };

  const getIngredientId = (i: Ingredient) => i.ID;

  const formatDate = formatTrialDate;

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "draft": return "Nacrt";
      case "confirmed": return "Potvrdjeno";
      case "in_progress": return "U tijeku";
      case "completed": return "Zavrseno";
      case "promoted": return "Promovirano";
      case "archived": return "Arhivirano";
      default: return status;
    }
  };

  const getTotalBakerPct = (ings: TrialIngredient[]) => sumBakerPct(ings);

  const handleSaveParameters = async () => {
    if (!selectedTrial || saving) return;

    setSaving(true);
    try {
      const data = {
        Trial: getTrialId(selectedTrial),
        FermentationType: paramForm.vrsta_fermentacije,
        DoughMassKg: paramForm.masa_zamjesa_kg ? parseFloat(paramForm.masa_zamjesa_kg) : undefined,
        DoughTempC: paramForm.temp_zamjes_cel ? parseFloat(paramForm.temp_zamjes_cel) : undefined,
        StarterPh: paramForm.ph_starter ? parseFloat(paramForm.ph_starter) : undefined,
        BulkFermentationPh1: paramForm.ph1_masa ? parseFloat(paramForm.ph1_masa) : null,
        BulkFermentationPh2: paramForm.ph2_masa ? parseFloat(paramForm.ph2_masa) : null,
        BulkFermentationHeight1: paramForm.h1_masa ? parseFloat(paramForm.h1_masa) : null,
        BulkFermentationHeight2: paramForm.h2_masa ? parseFloat(paramForm.h2_masa) : null,
        MixingStart: paramForm.t_start_mijesanja || null,
        MixingEnd: paramForm.t_kraj_mijesanja || null,
        BakingStart: paramForm.t_start_pecenje || null,
        BakingEnd: paramForm.t_kraj_pecenje || null,
        BakingTempC: paramForm.temp_pecenje_cel ? parseFloat(paramForm.temp_pecenje_cel) : null,
        SpecificVolumeLkg: paramForm.spec_vol_lkg ? parseFloat(paramForm.spec_vol_lkg) : null,
        Scrap: paramForm.skart ? parseInt(paramForm.skart) : null,
        ScrapColor: paramForm.skart_boja ? parseInt(paramForm.skart_boja) : null,
        ScrapShape: paramForm.skart_oblik ? parseInt(paramForm.skart_oblik) : null,
        Notes: paramForm.napomena || undefined,
      };

      await mesApi.createTrialParameter(data);
      toast.success("Parametri spremljeni");
      loadTrialDetails(selectedTrial);
    } catch (err: any) {
      toast.error("Greska pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsDone = async () => {
    if (!selectedTrial || saving) return;
    setSaving(true);
    try {
      await mesApi.updateTrial(getTrialId(selectedTrial), { Status: "completed" });
      toast.success("Pokus oznacen kao zavrsen");
      history.push("/mes/saved-trials");
    } catch (err: any) {
      toast.error("Greska: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePromoteTrial = async () => {
    if (!selectedTrial || saving) return;
    if (!promoteRecipeCode) {
      toast.error("Unesite kod recepta");
      return;
    }
    setSaving(true);
    try {
      await mesApi.promoteTrial(getTrialId(selectedTrial));
      toast.success("Pokus promoviran u recept!");
      history.push("/mes/saved-trials");
    } catch (err: any) {
      toast.error("Greska pri promociji: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePrintWorkOrder = () => {
    if (!selectedTrial) return;
    if (!printWorkOrder(selectedTrial, trialIngredients)) {
      toast.error("Nije moguce otvoriti prozor za ispis — provjerite blokadu popupa");
    }
  };

  const renderPageTabs = () => {
    if (!selectedTrial) return null;

    return (
      <>
        <header className="mes-section trial-detail-header">
          <button type="button" className="btn-secondary btn-sm" onClick={() => history.push("/mes/saved-trials")}>
            ← Spremljene probe
          </button>
          <div className="trial-detail-title">
            <h2 style={{ margin: 0 }}>{getTrialName(selectedTrial)}</h2>
            <p className="mes-hint" style={{ margin: "0.25rem 0 0" }}>
              {formatDate(selectedTrial.TrialDate)} · {selectedTrial.Product_ProductToTrial?.ProductName || "—"} ·{" "}
              <span className={`status-pill status-${selectedTrial.Status}`}>
                {getStatusLabel(selectedTrial.Status)}
              </span>
            </p>
          </div>
        </header>

        <div className="mes-section">
          <div className="kpi-tabs fq-analysis-tabs">
            {(["nalog", "parametri", "usporedi", "izvedba"] as TabType[]).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`kpi-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "nalog" ? "Nalog" : tab === "parametri" ? "Parametri" : tab === "usporedi" ? "Usporedi" : "Izvedba"}
              </button>
            ))}
          </div>
        </div>

        <div className="mes-section trial-detail-panel">
          {activeTab === "nalog" && renderNalogTab()}
          {activeTab === "parametri" && renderParametriTab()}
          {activeTab === "usporedi" && renderUsporediTab()}
          {activeTab === "izvedba" && renderIzvedbaTab()}
        </div>
      </>
    );
  };

  const renderNalogTab = () => (
    <>
      {/* Trial Info */}
      <div className="trial-info-grid">
        <div className="detail-item">
          <span className="detail-label">Kod</span>
          <span className="detail-value">{selectedTrial?.TrialCode || "-"}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Datum</span>
          <span className="detail-value">{formatDate(selectedTrial?.TrialDate)}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Proizvod</span>
          <span className="detail-value">{selectedTrial?.Product_ProductToTrial?.ProductName || "-"}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Masa tijesta</span>
          <span className="detail-value">{selectedTrial?.DoughMassKg || "-"} kg</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Status</span>
          <span className={`status-pill status-${selectedTrial?.Status}`}>
            <span className="status-dot"></span>
            {getStatusLabel(selectedTrial?.Status || "")}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">Ocjena</span>
          <span className="detail-value">{(selectedTrial as any)?.final_score !== null ? `${(selectedTrial as any)?.final_score}/10` : "-"}</span>
        </div>
      </div>

      {selectedTrial?.Purpose && (
        <div style={{ marginTop: "1rem", padding: "0.75rem", background: "rgba(75,153,135,0.1)", borderRadius: "6px" }}>
          <strong>Svrha:</strong> {selectedTrial.Purpose}
        </div>
      )}

      {/* Ingredients Table */}
      <h4 style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}>Receptura</h4>
      {trialIngredients.length > 0 ? (
        <div className="mes-table-wrap">
          <table className="mes-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Sirovina</th>
              <th>Tip</th>
              <th>PEK %</th>
              <th>Masa (kg)</th>
            </tr>
          </thead>
          <tbody>
            {trialIngredients
              .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
              .map((ing, idx) => (
                <tr key={ing.ID}>
                  <td>{idx + 1}</td>
                  <td>
                    {ing.IngredientName}
                    {ing.IsNewIngredient && <span className="new-ingredient-badge">NOVA</span>}
                  </td>
                  <td>{ing.IngredientType || "-"}</td>
                  <td className="percentage-value">{bakerPctNum(ing.BakerPercentage).toFixed(1)}%</td>
                  <td>{ing.MassKg != null ? Number(ing.MassKg).toFixed(3) : "-"}</td>
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}><strong>UKUPNO</strong></td>
              <td><strong>{getTotalBakerPct(trialIngredients).toFixed(1)}%</strong></td>
              <td><strong>{selectedTrial?.DoughMassKg || "-"} kg</strong></td>
            </tr>
          </tfoot>
        </table>
        </div>
      ) : (
        <p className="empty-state">Nema definiranih sastojaka.</p>
      )}

      {/* Print Button */}
      <div className="mes-actions">
        <button className="btn-secondary" onClick={handlePrintWorkOrder}>
          🖨️ Ispis radnog naloga
        </button>
      </div>
    </>
  );

  const renderParametriTab = () => (
    <>
      {trialParameters.length > 0 && (
        <div className="parameter-status-info" style={{ marginBottom: "1rem", padding: "0.75rem", background: "rgba(39,174,96,0.1)", borderRadius: "6px", color: "#27ae60" }}>
          ✓ Parametri vec uneseni ({formatDate((trialParameters[0] as any).CreateDate)})
        </div>
      )}

      {/* Parameter Entry Form */}
      <div className="form-group">
        <label>Vrsta fermentacije</label>
        <select
          value={paramForm.vrsta_fermentacije}
          onChange={(e) => setParamForm({ ...paramForm, vrsta_fermentacije: e.target.value })}
        >
          <option value="day">Dnevna</option>
          <option value="night">Nocna</option>
        </select>
      </div>

      <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem", color: "#4b9987" }}>Mikser</h4>
      <div className="form-grid-2">
        <div className="form-group">
          <label>Masa zamjesa (kg)</label>
          <input
            type="number"
            step="0.1"
            value={paramForm.masa_zamjesa_kg}
            onChange={(e) => setParamForm({ ...paramForm, masa_zamjesa_kg: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Temperatura (°C)</label>
          <input
            type="number"
            step="0.1"
            value={paramForm.temp_zamjes_cel}
            onChange={(e) => setParamForm({ ...paramForm, temp_zamjes_cel: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>pH starter</label>
          <input
            type="number"
            step="0.01"
            value={paramForm.ph_starter}
            onChange={(e) => setParamForm({ ...paramForm, ph_starter: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>pH zamjesa</label>
          <input
            type="number"
            step="0.01"
            value={paramForm.ph1_masa}
            onChange={(e) => setParamForm({ ...paramForm, ph1_masa: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Start mijesanja</label>
          <input
            type="time"
            value={paramForm.t_start_mijesanja}
            onChange={(e) => setParamForm({ ...paramForm, t_start_mijesanja: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Kraj mijesanja</label>
          <input
            type="time"
            value={paramForm.t_kraj_mijesanja}
            onChange={(e) => setParamForm({ ...paramForm, t_kraj_mijesanja: e.target.value })}
          />
        </div>
      </div>

      <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem", color: "#4b9987" }}>Fermentacija</h4>
      <div className="form-grid-2">
        <div className="form-group">
          <label>Visina 1 (mjerica)</label>
          <input
            type="number"
            value={paramForm.h1_masa}
            onChange={(e) => setParamForm({ ...paramForm, h1_masa: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Visina 2 (rheon)</label>
          <input
            type="number"
            value={paramForm.h2_masa}
            onChange={(e) => setParamForm({ ...paramForm, h2_masa: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>pH (Rheon)</label>
          <input
            type="number"
            step="0.01"
            value={paramForm.ph2_masa}
            onChange={(e) => setParamForm({ ...paramForm, ph2_masa: e.target.value })}
          />
        </div>
      </div>

      <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem", color: "#4b9987" }}>Pecenje</h4>
      <div className="form-grid-2">
        <div className="form-group">
          <label>Start pecenja</label>
          <input
            type="time"
            value={paramForm.t_start_pecenje}
            onChange={(e) => setParamForm({ ...paramForm, t_start_pecenje: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Kraj pecenja</label>
          <input
            type="time"
            value={paramForm.t_kraj_pecenje}
            onChange={(e) => setParamForm({ ...paramForm, t_kraj_pecenje: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Temperatura pecenja (°C)</label>
          <input
            type="number"
            value={paramForm.temp_pecenje_cel}
            onChange={(e) => setParamForm({ ...paramForm, temp_pecenje_cel: e.target.value })}
          />
        </div>
      </div>

      <h4 style={{ marginTop: "1rem", marginBottom: "0.5rem", color: "#4b9987" }}>Kvaliteta</h4>
      <div className="form-grid-2">
        <div className="form-group">
          <label>Spec. volumen (L/kg)</label>
          <input
            type="number"
            step="0.001"
            value={paramForm.spec_vol_lkg}
            onChange={(e) => setParamForm({ ...paramForm, spec_vol_lkg: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Skart (kom)</label>
          <input
            type="number"
            value={paramForm.skart}
            onChange={(e) => setParamForm({ ...paramForm, skart: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Skart boja (kom)</label>
          <input
            type="number"
            value={paramForm.skart_boja}
            onChange={(e) => setParamForm({ ...paramForm, skart_boja: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Skart oblik (kom)</label>
          <input
            type="number"
            value={paramForm.skart_oblik}
            onChange={(e) => setParamForm({ ...paramForm, skart_oblik: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group" style={{ marginTop: "1rem" }}>
        <label>Napomena</label>
        <textarea
          value={paramForm.napomena}
          onChange={(e) => setParamForm({ ...paramForm, napomena: e.target.value })}
          placeholder="Dodatne napomene..."
        />
      </div>

      <div className="mes-actions">
        <button className="btn-submit" onClick={handleSaveParameters} disabled={saving}>
          {saving ? "Spremanje..." : "Spremi parametre"}
        </button>
      </div>
    </>
  );

  const renderUsporediTab = () => {
    // Filter trials that can be compared (same product, different trial)
    const comparableTrials = trials.filter(
      (t) => getTrialId(t) !== getTrialId(selectedTrial!) && t.Product === selectedTrial?.Product
    );

    return (
      <>
        <div className="form-group">
          <label>Usporedi s pokusom</label>
          <select
            value={compareTrialId || ""}
            onChange={(e) => loadCompareTrialData(e.target.value ? Number(e.target.value) : 0)}
          >
            <option value="">Odaberi pokus za usporedbu</option>
            {comparableTrials.map((t) => (
              <option key={getTrialId(t)} value={getTrialId(t)}>
                {getTrialName(t)} - {formatDate(t.TrialDate)}
              </option>
            ))}
          </select>
        </div>

        {compareTrialData && (
          <>
            {/* Parameter Comparison */}
            {trialParameters.length > 0 && compareParameters.length > 0 && (
              <>
                <h4 style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}>Usporedba parametara</h4>
                <div className="mes-table-wrap">
                  <table className="mes-table comparison-table">
                  <thead>
                    <tr>
                      <th>Parametar</th>
                      <th>{getTrialName(selectedTrial!)}</th>
                      <th>{getTrialName(compareTrialData)}</th>
                      <th>Razlika</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "DoughMassKg", label: "Masa (kg)" },
                      { key: "DoughTempC", label: "Temperatura (°C)" },
                      { key: "StarterPh", label: "pH starter" },
                      { key: "SpecificVolumeLkg", label: "Spec. vol. (L/kg)" },
                    ].map(({ key, label }) => {
                      const val1 = (trialParameters[0] as any)[key];
                      const val2 = (compareParameters[0] as any)[key];
                      const diff = val1 && val2 ? (val1 - val2).toFixed(2) : "-";
                      return (
                        <tr key={key}>
                          <td>{label}</td>
                          <td>{val1 ?? "-"}</td>
                          <td>{val2 ?? "-"}</td>
                          <td className={Number(diff) > 0 ? "delta-positive" : Number(diff) < 0 ? "delta-negative" : ""}>
                            {diff !== "-" ? (Number(diff) > 0 ? "+" : "") + diff : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}

            {/* Ingredient Comparison */}
            <h4 style={{ marginTop: "1.5rem", marginBottom: "0.75rem" }}>Usporedba recepture</h4>
            <div className="mes-table-wrap">
              <table className="mes-table comparison-table">
              <thead>
                <tr>
                  <th>Sirovina</th>
                  <th>{getTrialName(selectedTrial!)} (%)</th>
                  <th>{getTrialName(compareTrialData)} (%)</th>
                  <th>Razlika</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Merge ingredient lists
                  const allIngredients = new Map<string, { name: string; pct1?: number; pct2?: number }>();
                  trialIngredients.forEach((i) => {
                    allIngredients.set(i.IngredientName, { name: i.IngredientName, pct1: i.BakerPercentage });
                  });
                  compareIngredients.forEach((i) => {
                    const existing = allIngredients.get(i.IngredientName);
                    if (existing) {
                      existing.pct2 = i.BakerPercentage;
                    } else {
                      allIngredients.set(i.IngredientName, { name: i.IngredientName, pct2: i.BakerPercentage });
                    }
                  });

                  return Array.from(allIngredients.values()).map((ing) => {
                    const diff = ing.pct1 !== undefined && ing.pct2 !== undefined
                      ? (ing.pct1 - ing.pct2).toFixed(2)
                      : "-";
                    return (
                      <tr key={ing.name}>
                        <td>{ing.name}</td>
                        <td>{ing.pct1 ?? "-"}</td>
                        <td>{ing.pct2 ?? "-"}</td>
                        <td className={Number(diff) > 0 ? "delta-positive" : Number(diff) < 0 ? "delta-negative" : ""}>
                          {diff !== "-" ? (Number(diff) > 0 ? "+" : "") + diff : "-"}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            </div>
          </>
        )}

        {!compareTrialData && comparableTrials.length === 0 && (
          <p className="empty-state" style={{ marginTop: "1rem" }}>
            Nema drugih pokusa za isti proizvod za usporedbu.
          </p>
        )}
      </>
    );
  };

  const renderIzvedbaTab = () => (
    <>
      {/* Current Status */}
      <div className={`mes-status-banner ${selectedTrial?.Status}`}>
        Trenutni status: <strong>{getStatusLabel(selectedTrial?.Status || "")}</strong>
      </div>

      {selectedTrial?.Status === "draft" && (
        <div style={{ marginTop: "1.5rem" }}>
          <p style={{ marginBottom: "1rem" }}>
            Pokus je u nacrtu. Mozete ga oznaciti kao zavrsen nakon sto unesete parametre.
          </p>
          <button className="btn-success" onClick={handleMarkAsDone} disabled={saving}>
            ✓ Oznaci kao zavrseno
          </button>
        </div>
      )}

      {(selectedTrial?.Status === "completed" || selectedTrial?.Status === "confirmed") && (
        <div style={{ marginTop: "1.5rem" }}>
          <p style={{ marginBottom: "1rem" }}>
            Pokus je zavrsen. Mozete ga promovirati u produkcijski recept.
          </p>
          <div className="form-group">
            <label>Kod novog recepta</label>
            <input
              type="text"
              value={promoteRecipeCode}
              onChange={(e) => setPromoteRecipeCode(e.target.value)}
              placeholder="npr. REC-001"
            />
          </div>
          <button className="btn-primary" onClick={handlePromoteTrial} disabled={saving}>
            🚀 Promoviraj u produkciju
          </button>
        </div>
      )}

      {selectedTrial?.Status === "promoted" && (
        <div style={{ marginTop: "1.5rem" }}>
          <p style={{ color: "#27ae60" }}>
            ✓ Ovaj pokus je vec promoviran u produkcijski recept.
          </p>
        </div>
      )}

      {/* Trial Notes */}
      {selectedTrial?.Notes && (
        <div style={{ marginTop: "1.5rem" }}>
          <h4>Biljeske</h4>
          <p style={{ padding: "0.75rem", background: "rgba(255,255,255,0.05)", borderRadius: "6px" }}>
            {selectedTrial.Notes}
          </p>
        </div>
      )}
    </>
  );

  return (
    <div className="mes-page saved-trials-page trial-detail-page">
      <Header title="MES - Detalj probe" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {loading && <div className="mes-section loading-state">Ucitavanje...</div>}
          {!loading && !selectedTrial && (
            <div className="mes-section empty-state">
              <p>Proba nije pronadena.</p>
              <button type="button" className="btn-secondary" onClick={() => history.push("/mes/saved-trials")}>
                Natrag na listu
              </button>
            </div>
          )}
          {!loading && selectedTrial && renderPageTabs()}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default TrialDetailPage;
